/**
 * Returns a Set of lowercased basenames for every file entry in a JSZip instance.
 * Ignores directories and files starting with "._" (common macOS metadata files).
 * @param {import("jszip")} zip
 * @returns {Set<string>}
 *
 * @example
 * const zip = await JSZip.loadAsync(file);
 * const filenames = getFilenamesFromZip(zip);
 * // If the zip contains "Document1.pdf", "Document2.pdf", and "._MACOSX/metadata.txt",
 * // the returned Set will be: new Set(["document1.pdf", "document2.pdf"])
 */
export function getFilenamesFromZip(zip) {
  const filenames = new Set();
  zip.forEach((relativePath, zipEntry) => {
    if (!zipEntry.dir) {
      const basename = relativePath.split("/").pop().toLowerCase();
      if (!basename.startsWith("._")) {
        filenames.add(basename);
      }
    }
  });
  return filenames;
}

/**
 * Returns true if the given filename is present in the zip filenames set.
 * Also matches when the filename lacks a .pdf extension and a .pdf variant exists.
 * @param {string} filename
 * @param {Set<string>} zipFilenames
 * @returns {boolean}
 *
 * @example
 * inZip("document1", new Set(["document1.pdf", "document2.pdf"])) // true
 * inZip("document1.pdf", new Set(["document1.pdf", "document2.pdf"])) // true
 * inZip("document3", new Set(["document1.pdf", "document2.pdf"])) // false
 */
export function inZip(filename, zipFilenames) {
  const normalized = filename.toLowerCase();
  return (
    zipFilenames.has(normalized) ||
    (!normalized.endsWith(".pdf") && zipFilenames.has(`${normalized}.pdf`))
  );
}

/**
 * Matches manifest filenames against zip contents.
 * @param {string[]} manifestFilenames
 * @param {Set<string>} zipFilenames
 * @returns {{ found: number, total: number, records: Array<{ filename: string, inZip: boolean }> }}
 *
 * @example
 * matchManifestToZip(["doc1", "doc2"], new Set(["doc1.pdf", "doc3.pdf"]))
 * // Returns: { found: 1, total: 2, records: [{ filename: "doc1", inZip: true }, { filename: "doc2", inZip: false }] }
 */
export function matchManifestToZip(manifestFilenames, zipFilenames) {
  const records = manifestFilenames.map((filename) => ({
    filename,
    inZip: inZip(filename, zipFilenames),
  }));

  return {
    found: records.filter((r) => r.inZip).length,
    total: manifestFilenames.length,
    records,
  };
}
